import {
  PullRequestDiffResult,
  type PullRequestDiffInput,
  type PullRequestDiffResult as PullRequestDiffResultType,
} from "@cmmd-forge/contracts";
import * as Schema from "effect/Schema";

const decodePullRequestDiffResult = Schema.decodeUnknownSync(PullRequestDiffResult);

export class PullRequestDiffHttpError extends Error {
  readonly status: number | null;
  readonly code: "timeout" | "network" | "response" | "decode";

  constructor(input: {
    readonly code: PullRequestDiffHttpError["code"];
    readonly message: string;
    readonly status?: number | null;
    readonly cause?: unknown;
  }) {
    super(input.message, { cause: input.cause });
    this.name = "PullRequestDiffHttpError";
    this.code = input.code;
    this.status = input.status ?? null;
  }
}

export function pullRequestDiffUrl(httpBaseUrl: string): string {
  const url = new URL(httpBaseUrl);
  const basePath = url.pathname.endsWith("/") ? url.pathname : `${url.pathname}/`;
  url.pathname = `${basePath}api/pull-requests/diff`.replaceAll(/\/{2,}/g, "/");
  url.search = "";
  url.hash = "";
  return url.toString();
}

export async function fetchPullRequestDiff(input: {
  readonly httpBaseUrl: string;
  readonly payload: PullRequestDiffInput;
  /** Environment session bearer only; runtime/control-plane grants are not valid here. */
  readonly bearerToken?: string;
  readonly timeoutMs?: number;
  readonly fetchImpl?: typeof fetch;
}): Promise<PullRequestDiffResultType> {
  const abort = new AbortController();
  const timeout = setTimeout(() => abort.abort(), input.timeoutMs ?? 30_000);
  let response: Response;
  try {
    response = await (input.fetchImpl ?? fetch)(pullRequestDiffUrl(input.httpBaseUrl), {
      method: "POST",
      credentials: input.bearerToken === undefined ? "include" : "omit",
      headers: {
        "content-type": "application/json",
        ...(input.bearerToken === undefined
          ? {}
          : { authorization: `Bearer ${input.bearerToken}` }),
      },
      body: JSON.stringify(input.payload),
      signal: abort.signal,
    });
  } catch (cause) {
    throw new PullRequestDiffHttpError({
      code: abort.signal.aborted ? "timeout" : "network",
      message: abort.signal.aborted
        ? "Pull request diff request timed out."
        : "Pull request diff request failed.",
      cause,
    });
  } finally {
    clearTimeout(timeout);
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch (cause) {
    throw new PullRequestDiffHttpError({
      code: "decode",
      message: "Pull request diff response was not valid JSON.",
      status: response.status,
      cause,
    });
  }
  if (!response.ok) {
    const message =
      typeof body === "object" &&
      body !== null &&
      "message" in body &&
      typeof body.message === "string"
        ? body.message
        : `Pull request diff request failed with HTTP ${response.status}.`;
    throw new PullRequestDiffHttpError({ code: "response", message, status: response.status });
  }
  try {
    return decodePullRequestDiffResult(body);
  } catch (cause) {
    throw new PullRequestDiffHttpError({
      code: "decode",
      message: "Pull request diff response did not match the contract.",
      status: response.status,
      cause,
    });
  }
}
